import type { Context, Liquid } from "@hightouch/lib/templating/liquid";

import { LIQUID_HASH_ENTRY } from "../types/dynamic-slot-markers";

/**
 * Render-context key holding prefetched content-block HTML, keyed by lowercased
 * uuid (the tag normalizes ids so source/DB case differences don't miss-render).
 * Callers pass the map via `renderCepTemplate`'s `contentBlocks` param, not this
 * key directly. The tag reads it without DB access, staying sync-safe.
 */
export const CONTENT_BLOCKS_CONTEXT_KEY = "__contentBlocks";

function getContentBlockMap(ctx: Context): Record<string, string> | undefined {
  return ctx.get([CONTENT_BLOCKS_CONTEXT_KEY]) as
    | Record<string, string>
    | undefined;
}

/** A content block id: the UUID shape the tag matcher accepts. Case tolerance comes from the matcher's `i` flag. */
export const CONTENT_BLOCK_ID_PATTERN = String.raw`[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}`;

/**
 * Matches `{% ht_content_block id:"<uuid>" %}`, the tag registered below. Tolerates
 * whitespace-trim variants, quote styles, and extra `key:value` hash entries
 * around `id`, so a future field doesn't make it silently miss tags. Fresh
 * instance per call: the `g` flag makes `lastIndex` stateful. `idPattern`
 * widens which quoted ids match (capture group 1).
 */
export const buildLiquidContentBlockTagRegex = (
  idPattern: string = CONTENT_BLOCK_ID_PATTERN,
): RegExp =>
  new RegExp(
    String.raw`\{%-?\s*ht_content_block(?:\s+${LIQUID_HASH_ENTRY})*\s+id\s*:\s*["'](${idPattern})["'](?:\s+${LIQUID_HASH_ENTRY})*\s*-?%\}`,
    "gi",
  );

/**
 * {@link buildLiquidContentBlockTagRegex} for a tag with any quoted id, capturing it.
 * A quoted id that is not a UUID still parses and sends but renders empty, so a check of an email's tags must see it too.
 */
export const buildLiquidContentBlockTagAnyIdRegex = (): RegExp =>
  buildLiquidContentBlockTagRegex(String.raw`[^"']*`);

/** The canonical `{% ht_content_block id:"<uuid>" %}` reference to block `id`. */
export const buildContentBlockTag = (id: string): string =>
  `{% ht_content_block id:"${id}" %}`;

/**
 * Registers the `{% ht_content_block id:"<uuid>" %}` tag, resolving to prefetched
 * HTML from the `__contentBlocks` key; unknown id renders empty. Uses liquidjs's
 * colon hash syntax (`id:"..."`), the only form its hash parser recognizes. The
 * `parse` hook rejects missing/unquoted ids — the prefetch regex matches only
 * quoted literals, so they'd silently render empty otherwise.
 */
export function registerContentBlockTag(engine: Liquid): void {
  engine.registerTag("ht_content_block", {
    parse(token) {
      const args = token.args;
      const hasIdKey = /(?:^|\s)id\s*:/.test(args);
      if (!hasIdKey) {
        throw new Error(
          'ht_content_block tag requires an id. Use {% ht_content_block id:"<uuid>" %}.',
        );
      }
      const hasQuotedIdValue = /(?:^|\s)id\s*:\s*["']/.test(args);
      if (!hasQuotedIdValue) {
        throw new Error(
          'ht_content_block tag requires a literal quoted id. Use {% ht_content_block id:"<uuid>" %}.',
        );
      }
    },
    render(ctx, emitter, hash) {
      const args = hash as unknown as Record<string, unknown>;
      const rawId = args.id;
      if (typeof rawId !== "string") return;

      const id = rawId.toLowerCase();
      const blocks = getContentBlockMap(ctx);
      const html = blocks?.[id];
      if (html === undefined) return;

      // Render through the same engine + context so Liquid vars inside the block
      // resolve against per-recipient data; ctx.getAll() carries __contentBlocks
      // so nested block refs resolve too.
      const rendered = engine.parseAndRenderSync(html, ctx.getAll());
      emitter.write(rendered);
    },
  });
}
